// src/components/common/QRScanner.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5QrcodeScanner, Html5QrcodeScanType } from 'html5-qrcode';
import { X, Camera, Upload } from 'lucide-react';
import Button from './Button';

const QRScanner = ({ onScan, onClose, isOpen }) => {
    const scannerRef = useRef(null);
    const [error, setError] = useState(null);
    const [isScanning, setIsScanning] = useState(false);

    // Memoizar onScan para evitar re-renders
    const handleScanSuccess = useCallback((decodedText) => {
        console.log('QR Scanned:', decodedText);
        onScan(decodedText);
    }, [onScan]);

    useEffect(() => {
        if (!isOpen) {
            // Limpiar cuando se cierra
            if (scannerRef.current) {
                scannerRef.current.clear().catch(() => {});
                scannerRef.current = null;
            }
            setIsScanning(false);
            return;
        }

        // Evitar inicializar múltiples veces
        if (isScanning || scannerRef.current) return;

        const config = {
            fps: 10,
            qrbox: {
                width: 250,
                height: 250
            },
            supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA]
        };

        const html5QrScanner = new Html5QrcodeScanner(
            "qr-scanner-container",
            config,
            false
        );

        const onScanSuccessHandler = (decodedText, decodedResult) => {
            // Limpiar el scanner antes de llamar onScan
            html5QrScanner.clear().then(() => {
                scannerRef.current = null;
                setIsScanning(false);
                handleScanSuccess(decodedText);
            }).catch(console.error);
        };

        const onScanFailure = (error) => {
            // Ignorar errores de escaneo normales
            if (error && error.includes && error.includes('NotFoundException')) {
                return;
            }
        };

        try {
            setIsScanning(true);
            html5QrScanner.render(onScanSuccessHandler, onScanFailure);
            scannerRef.current = html5QrScanner;
            setError(null);
        } catch (err) {
            console.error('Error inicializando scanner:', err);
            setError('Error al inicializar la cámara. Por favor, verifica los permisos.');
            setIsScanning(false);
        }

        return () => {
            if (scannerRef.current) {
                scannerRef.current.clear().catch(() => {});
                scannerRef.current = null;
            }
            setIsScanning(false);
        };
    }, [isOpen, handleScanSuccess, isScanning]);

    // Manejar cierre manual
    const handleClose = useCallback(() => {
        if (scannerRef.current) {
            scannerRef.current.clear().then(() => {
                scannerRef.current = null;
                setIsScanning(false);
                onClose();
            }).catch(() => {
                scannerRef.current = null;
                setIsScanning(false);
                onClose();
            });
        } else {
            onClose();
        }
    }, [onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-900">Escanear Código QR</h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 p-1"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                {error ? (
                    <div className="text-center py-8">
                        <Camera className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-red-600 mb-4">{error}</p>
                        <Button onClick={handleClose} variant="outline">
                            Cerrar
                        </Button>
                    </div>
                ) : (
                    <>
                        {/* Scanner Container */}
                        <div className="mb-4">
                            <div id="qr-scanner-container" className="w-full"></div>
                        </div>

                        {/* Instructions */}
                        <div className="text-center">
                            <p className="text-sm text-gray-600 mb-4">
                                Apunta la cámara hacia el código QR del activo
                            </p>

                            <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
                                <Camera className="h-4 w-4" />
                                <span>Cámara activa</span>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="mt-4 flex justify-end">
                            <Button onClick={handleClose} variant="outline">
                                Cancelar
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default QRScanner;